/**
 * Parse job page HTML into structured job offer + text for ATS/keyword analysis.
 * Indeed-specific selectors are used when the URL is from Indeed; otherwise generic fallback.
 *
 * Designed to run in the browser (uses DOMParser). Server returns raw HTML; client calls this.
 */

import type { ParsedJobOffer } from '../types';

/** Aligné avec AI_MAX_JD_LENGTH pour ne pas tronquer l'annonce avant analyse/diagnostics. */
const MAX_DESCRIPTION_LENGTH = 35_000;
const MIN_TEXT_LENGTH = 100;

/**
 * Build a single analysis text from a parsed offer (for ATS/IA).
 * Order: titre, entreprise, lieu, salaire, type, compétences, missions, profil, description.
 */
export function parsedOfferToAnalysisText(offer: ParsedJobOffer): string {
  const parts: string[] = [];
  if (offer.titre?.trim()) parts.push(`Titre du poste : ${offer.titre.trim()}`);
  if (offer.entreprise?.trim()) parts.push(`Entreprise : ${offer.entreprise.trim()}`);
  if (offer.lieu?.trim()) parts.push(`Lieu : ${offer.lieu.trim()}`);
  if (offer.salaire?.trim()) parts.push(`Salaire : ${offer.salaire.trim()}`);
  if (offer.typePoste?.trim()) parts.push(`Type de poste : ${offer.typePoste.trim()}`);
  if (offer.competencesRequises?.length) {
    const s = Array.isArray(offer.competencesRequises)
      ? offer.competencesRequises.join(', ')
      : String(offer.competencesRequises);
    if (s.trim()) parts.push(`Compétences requises : ${s.trim()}`);
  }
  if (offer.missionsPrincipales?.trim()) parts.push(`Missions principales :\n${offer.missionsPrincipales.trim()}`);
  if (offer.profilRecherche?.trim()) parts.push(`Profil recherché :\n${offer.profilRecherche.trim()}`);
  if (offer.qualitesHumaines?.trim()) parts.push(`Qualités humaines :\n${offer.qualitesHumaines.trim()}`);
  if (offer.avantages?.trim()) parts.push(`Avantages :\n${offer.avantages.trim()}`);
  if (offer.secteur?.trim()) parts.push(`Secteur : ${offer.secteur.trim()}`);
  if (offer.aProposEntreprise?.trim()) parts.push(`À propos de l'entreprise :\n${offer.aProposEntreprise.trim()}`);
  if (offer.description?.trim()) parts.push(`Description :\n${offer.description.trim()}`);
  const full = parts.join('\n\n').replace(/\n\s*\n/g, '\n\n').trim();
  return full.substring(0, MAX_DESCRIPTION_LENGTH);
}

function isIndeedUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    return host === 'indeed.com' || host.endsWith('.indeed.com');
  } catch {
    return false;
  }
}

/** Detect Cloudflare / anti-bot challenge pages so we don't treat them as job content. */
export function isBlockedOrChallengePage(html: string): boolean {
  const lower = html.toLowerCase();
  return (
    (lower.includes('cloudflare') && (lower.includes('verification') || lower.includes('challenge') || lower.includes('checking your browser'))) ||
    lower.includes('additional verification required') ||
    lower.includes('cf-browser-verification') ||
    lower.includes('captcha') && lower.includes('human')
  );
}

function getText(el: Element | null): string {
  if (!el) return '';
  return (el.textContent || '').trim().replace(/\s+/g, ' ');
}

/** Tous les en-têtes de section reconnus (FR + EN). Ordre : plus spécifique en premier pour éviter qu'un titre court mange le contenu. */
const SECTION_HEADER_REGEX = [
  { key: 'missionsPrincipales' as const, re: /(?:les\s+)?missions?\s*(?:du\s+poste|principales?)?\s*[:-]?\s*\n?/i },
  { key: 'missionsPrincipales' as const, re: /(?:rôle|votre\s+mission|vos\s+missions?|responsabilités?)\s*[:-]?\s*\n?/i },
  { key: 'missionsPrincipales' as const, re: /^mission\s*[:-]?\s*\n?/im },
  { key: 'missionsPrincipales' as const, re: /^missions\s*[:-]?\s*\n?/im },
  { key: 'profilRecherche' as const, re: /profil\s*(?:recherché|souhaité)?\s*[:-]?\s*\n?/i },
  { key: 'profilRecherche' as const, re: /(?:vous\s+êtes|qualifications?|profil\s*recherché)\s*[:-]?\s*\n?/i },
  { key: 'profilRecherche' as const, re: /^profil\s*[:-]?\s*\n?/im },
  { key: 'competencesRequises' as const, re: /compétences?\s*(?:requises?|souhaitées?|clés)?\s*[:-]?\s*\n?/i },
  { key: 'competencesRequises' as const, re: /(?:savoir-faire|skills?|expertise)\s*[:-]?\s*\n?/i },
  { key: 'qualitesHumaines' as const, re: /qualités?\s*humaines?\s*[:-]?\s*\n?/i },
  { key: 'avantages' as const, re: /(?:avantages?|nous\s+offrons|benefits?|ce\s+que\s+nous\s+offrons)\s*[:-]?\s*\n?/i },
  { key: 'aProposEntreprise' as const, re: /(?:à\s+propos\s+de\s+l'?entreprise|bienvenue\s+chez|about\s+(?:the\s+)?company|qui\s+sommes-nous)\s*[:-]?\s*\n?/i },
  { key: 'aProposEntreprise' as const, re: /(?:l'?entreprise|our\s+company)\s*[:-]?\s*\n?/i },
];

interface SectionMatch { index: number; endIndex: number; key: keyof ParsedJobOffer }
function findSectionBounds(description: string): SectionMatch[] {
  const matches: SectionMatch[] = [];
  for (const { key, re } of SECTION_HEADER_REGEX) {
    let m: RegExpExecArray | null;
    const reCopy = new RegExp(re.source, 'gi');
    while ((m = reCopy.exec(description)) !== null) {
      const start = m.index;
      const end = start + m[0].length;
      matches.push({ index: start, endIndex: end, key });
    }
  }
  matches.sort((a, b) => a.index - b.index);
  // Dédupliquer les chevauchements : garder la première occurrence à chaque position
  const deduped: SectionMatch[] = [];
  for (const m of matches) {
    if (deduped.length === 0 || m.index >= deduped[deduped.length - 1].endIndex) {
      deduped.push(m);
    }
  }
  return deduped;
}

function splitDescriptionSections(description: string): Partial<ParsedJobOffer> & { competencesRequises: string[] } {
  const result: Partial<ParsedJobOffer> & { competencesRequises: string[] } = { competencesRequises: [] };
  const bounds = findSectionBounds(description);
  const len = description.length;
  for (let i = 0; i < bounds.length; i++) {
    const start = bounds[i].endIndex;
    const end = i + 1 < bounds.length ? bounds[i + 1].index : len;
    let text = description.slice(start, end).trim();
    if (text.length < 15) continue;
    const MAX_SECTION_LENGTH = 8000;
    text = text.substring(0, MAX_SECTION_LENGTH);
    const key = bounds[i].key;
    if (key === 'competencesRequises') {
      if (!result.competencesRequises.length) {
        const items = text.split(/[\n•·–—-]/).map(s => s.trim()).filter(Boolean).slice(0, 40);
        if (items.length) result.competencesRequises = items;
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } else if (!(result as any)[key]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (result as any)[key] = text;
    }
  }
  return result;
}

/**
 * Indeed viewjob page: extract all key fields.
 * Structure may change (A/B, locale); multiple selectors per field.
 */
function parseIndeedPage(doc: Document): ParsedJobOffer {
  const offer: ParsedJobOffer = {};

  // 1) Titre
  const titleSelectors = [
    'h1.jobsearch-JobInfoHeader-title',
    '.jobsearch-JobInfoHeader h1',
    '[data-testid="jobsearch-JobInfoHeader"] h1',
    'h1[class*="JobInfoHeader"]',
    'h1',
  ];
  for (const sel of titleSelectors) {
    const el = doc.querySelector(sel);
    if (el) {
      const t = getText(el);
      if (t && t.length < 200) { offer.titre = t; break; }
    }
  }

  // 2) Entreprise (lien cliquable sous le titre)
  const companySelectors = [
    '[data-testid="inlineHeader-companyName"]',
    '.jobsearch-CompanyInfoContainer a',
    '.jobsearch-InlineCompanyRating a',
    'div[class*="CompanyInfo"] a',
    'a[data-tn-element="companyName"]',
  ];
  for (const sel of companySelectors) {
    const el = doc.querySelector(sel);
    if (el) {
      const t = getText(el);
      if (t && t.length < 150) { offer.entreprise = t; break; }
    }
  }

  // 3) Lieu
  const locationSelectors = [
    '[data-testid="job-location"]',
    '.jobsearch-JobInfoHeader-subtitle',
    '[class*="jobsearch-JobInfoHeader"] [class*="location"]',
    'div[class*="location"]',
    '[class*="companyLocation"]',
  ];
  for (const sel of locationSelectors) {
    const el = doc.querySelector(sel);
    if (el) {
      const t = getText(el);
      if (t && t.length < 200) { offer.lieu = t; break; }
    }
  }

  // 4) Salaire
  const salarySelectors = [
    '[id*="salary"]',
    '[data-testid*="salary"]',
    '[class*="salary"]',
    '*[class*="Salary"]',
  ];
  for (const sel of salarySelectors) {
    const el = doc.querySelector(sel);
    if (el) {
      const t = getText(el);
      if (t && (/\d|€|k€|euro|salaire/i.test(t)) && t.length < 150) { offer.salaire = t; break; }
    }
  }
  if (!offer.salaire) {
    const meta = doc.querySelector('[class*="jobsearch-JobMetadata"]');
    if (meta) {
      const metaText = getText(meta);
      const salaryMatch = metaText.match(/(?:salaire|rémunération|€|k€)[^·•]*[\d\s€,.]+(?:\s*€)?/i);
      if (salaryMatch) offer.salaire = salaryMatch[0].trim();
    }
  }

  // 5) Type de poste (CDI, CDD, etc.)
  const jobTypeSelectors = [
    '[class*="jobsearch-JobMetadata"]',
    '[data-testid*="job-details"]',
    '*[class*="employment"]',
  ];
  const jobTypeKeywords = /(?:CDI|CDD|stage|freelance|alternance|temps partiel|temps plein|contract|full-time|part-time|internship)/i;
  for (const sel of jobTypeSelectors) {
    const el = doc.querySelector(sel);
    if (el) {
      const t = getText(el);
      const match = t.match(jobTypeKeywords);
      if (match) { offer.typePoste = match[0].trim(); break; }
    }
  }

  // 6) Description principale
  const descSelectors = [
    '#jobDescriptionText',
    '#job-description',
    '[id*="jobDescription"]',
    '.jobsearch-JobComponent-description',
    '[data-testid="job-description"]',
    'div.jobsearch-jobDescriptionText',
  ];
  let description = '';
  for (const sel of descSelectors) {
    const el = doc.querySelector(sel);
    if (el) {
      description = getText(el);
      if (description.length >= 50) break;
    }
  }
  if (!description) {
    const fallback = doc.querySelector('[class*="jobDescription"], [class*="JobDescription"]');
    if (fallback) description = getText(fallback);
  }
  if (description) {
    offer.description = description;
    const sections = splitDescriptionSections(description);
    if (sections.missionsPrincipales) offer.missionsPrincipales = sections.missionsPrincipales;
    if (sections.profilRecherche) offer.profilRecherche = sections.profilRecherche;
    if (sections.competencesRequises?.length) offer.competencesRequises = sections.competencesRequises;
    if (sections.qualitesHumaines) offer.qualitesHumaines = sections.qualitesHumaines;
    if (sections.avantages) offer.avantages = sections.avantages;
    if (sections.aProposEntreprise) offer.aProposEntreprise = sections.aProposEntreprise;
  }

  return offer;
}

/**
 * Generic fallback: main content area, then body. Returns ParsedJobOffer with description only.
 */
function parseGenericPage(doc: Document): ParsedJobOffer {
  const toRemove = doc.querySelectorAll(
    'script, style, nav, footer, header, aside, iframe, noscript'
  );
  toRemove.forEach((el) => el.remove());

  const mainSelectors = [
    'main',
    'article',
    '[role="main"]',
    '.job-description',
    '.description',
    '#job-description',
    '.job-description-container',
    '[class*="job-description"]',
    '#content',
    '.content',
  ];
  for (const sel of mainSelectors) {
    const el = doc.querySelector(sel);
    if (el) {
      const text = getText(el);
      if (text.length >= MIN_TEXT_LENGTH) {
        return { description: text.replace(/\n\s*\n/g, '\n\n').substring(0, MAX_DESCRIPTION_LENGTH) };
      }
    }
  }

  const bodyText = getText(doc.body);
  if (bodyText.length >= MIN_TEXT_LENGTH) {
    return { description: bodyText.replace(/\n\s*\n/g, '\n\n').substring(0, MAX_DESCRIPTION_LENGTH) };
  }
  return {};
}

export interface ParseJobPageResult {
  parsed: ParsedJobOffer | null;
  text: string;
  source: 'indeed' | 'generic';
  ok: boolean;
}

/**
 * Parse job page HTML. Prefer Indeed-specific extraction when URL is Indeed.
 * Uses DOMParser – call from browser only.
 */
export function parseJobPage(html: string, pageUrl: string): ParseJobPageResult {
  if (isBlockedOrChallengePage(html)) {
    return { parsed: null, text: '', source: isIndeedUrl(pageUrl) ? 'indeed' : 'generic', ok: false };
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const isIndeed = isIndeedUrl(pageUrl);

  const parsed: ParsedJobOffer | null = isIndeed
    ? parseIndeedPage(doc)
    : parseGenericPage(doc);

  const text = parsed ? parsedOfferToAnalysisText(parsed) : '';
  if (text.length >= MIN_TEXT_LENGTH) {
    return { parsed, text, source: isIndeed ? 'indeed' : 'generic', ok: true };
  }

  if (isIndeed) {
    const genericParsed = parseGenericPage(parser.parseFromString(html, 'text/html'));
    const genericText = parsedOfferToAnalysisText(genericParsed);
    if (genericText.length >= MIN_TEXT_LENGTH) {
      return { parsed: genericParsed, text: genericText, source: 'generic', ok: true };
    }
  }

  return { parsed: null, text: '', source: isIndeed ? 'indeed' : 'generic', ok: false };
}
